import { useQuery } from "@apollo/client/react";
import {
  Outlet,
  createFileRoute,
  useMatchRoute,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import type {
  GetSeriesDetailsQuery,
  GetSeriesDetailsQueryVariables,
} from "@/codegen/graphql/generated/graphql";
import { GET_SERIES_DETAILS } from "@/codegen/graphql/queries/GetSeriesDetails.query";
import { useTracking } from "@/components/ClientAnalytics";
import NotFound from "@/components/NotFound";
import useShowMetaTags from "@/hooks/useShowMetaTags";
import { loadShowOrNotFound } from "@/lib/loadShowOrNotFound";
import SeriesBtsSkeleton from "@/modules/SeriesDetails/SeriesBtsSkeleton";
import SeriesCastSkeleton from "@/modules/SeriesDetails/SeriesCastSkeleton";
import SeriesDetailsSkeleton from "@/modules/SeriesDetails/SeriesDetailsSkeleton";
import SeriesEpisodesSkeleton from "@/modules/SeriesDetails/SeriesEpisodesSkeleton";
import SeriesRecommendationsSkeleton from "@/modules/SeriesDetails/SeriesRecommendationsSkeleton";
import { AmplitudeTrackingEvent } from "@/types/amplitude-tracking.type";

// The series details page and its sub-pages (SHO-757). Same loader contract
// as the player's: see `loadShowOrNotFound`.
export const Route = createFileRoute("/_default/series/$series")({
  loader: ({ params, preload, location }) =>
    loadShowOrNotFound(GET_SERIES_DETAILS, params.series, {
      preload,
      location,
      target: "series",
    }),
  notFoundComponent: NotFound,
  // No delay, like the player: the loader gates resolution on the fetch.
  pendingMs: 0,
  pendingMinMs: 0,
  pendingComponent: SeriesPending,
  component: SeriesLayout,
});

// The layout is what waits, for the page and its sub-pages alike: each View
// all gets its own skeleton, not the page's hero.
function SeriesPending() {
  const matchRoute = useMatchRoute();

  if (matchRoute({ to: "/series/$series/cast" })) return <SeriesCastSkeleton />;
  if (matchRoute({ to: "/series/$series/episodes" }))
    return <SeriesEpisodesSkeleton />;
  if (matchRoute({ to: "/series/$series/behind-the-scenes" }))
    return <SeriesBtsSkeleton />;
  if (matchRoute({ to: "/series/$series/recommendations" }))
    return <SeriesRecommendationsSkeleton />;
  return <SeriesDetailsSkeleton />;
}

// Owns the `<head>` while the series or one of its sub-pages is open: the
// loader applied the tags, this tears them down on the way out (SPA routes
// share one `<head>`), exactly like the player. On the layout, not the page,
// so moving to View all and back keeps them.
function SeriesLayout() {
  const { showId } = Route.useLoaderData();
  const { data } = useQuery<
    GetSeriesDetailsQuery,
    GetSeriesDetailsQueryVariables
  >(GET_SERIES_DETAILS, { variables: { showId } });

  useShowMetaTags(data?.show, "series");
  useTrackSeriesDetailsOpened(data?.show);

  return <Outlet />;
}

/**
 * `open series details`, once per series opened. Here rather than in the
 * page: the layout stays mounted across the page and its sub-pages, so a
 * return from View all — a fresh history entry with no state — doesn't
 * count again as an `seo` landing, which is the very split the event exists
 * for. The ref also absorbs StrictMode's double effect in dev.
 */
function useTrackSeriesDetailsOpened(
  show: { id: string; title: string; displayTitle: string } | undefined,
) {
  const { trackAmplitudeEvent } = useTracking();
  const origin = useRouterState({
    select: (s) => s.location.state.seriesDetailsOrigin,
  });
  const trackedShowId = useRef<string | null>(null);

  useEffect(() => {
    if (!show || trackedShowId.current === show.id) return;
    trackedShowId.current = show.id;
    trackAmplitudeEvent(AmplitudeTrackingEvent.openSeriesDetails, {
      "series name": show.title,
      "displayed series name": show.displayTitle,
      // No state: the visitor landed on the URL itself (search, a shared
      // link). Every in-app link sets its own (`SeriesLink`, the player).
      origin: origin ?? "seo",
    });
  }, [show?.id]);
}
